<template>
    <div>
        <div>
            <h1 class="text-3xl font-bold text-theme-base text-center mt-4">
                Ventas Por Producto
            </h1>
            <p class="mt-4">
                En esta sección podrás consultar las ventas de un producto en el
                periodo de tiempo que configures. Recuerda que una venta es una
                orden con estado de entrega completado.
            </p>
        </div>

        <div v-if="!idSku" class="mt-6 max-w-xl mx-auto">
            <products-search
                v-model="selectedProduct"
                label="Busca el producto que quieres consultar"
                @input="onProductSelected"
            />
        </div>

        <template v-else>
            <div class="mt-6 mb-4">
                <div class="product-info-card rounded-lg shadow-lg p-6">
                    <div
                        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
                    >
                        <div class="flex-1">
                            <a
                                :href="`/app/products/detail/${productCurrent.id}`"
                                target="_blank"
                                rel="noopener"
                            >
                                <h2
                                    class="text-2xl font-bold text-theme-base hover:text-theme-primary transition-colors hover:underline"
                                >
                                    {{ productCurrent.name }}
                                </h2>
                            </a>
                            <div class="mt-3 flex flex-wrap items-center gap-3">
                                <span class="product-badge">
                                    <svg
                                        class="w-4 h-4 mr-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                        />
                                    </svg>
                                    <span class="font-medium">SKU:</span>
                                    {{ productCurrent.sku || "NA" }}
                                </span>
                                <span class="product-badge">
                                    <svg
                                        class="w-4 h-4 mr-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                        />
                                    </svg>
                                    <span class="font-medium">ID:</span>
                                    {{ productCurrent.id }}
                                </span>
                            </div>
                        </div>
                        <div
                            class="flex items-center justify-center lg:justify-end gap-x-4"
                        >
                            <div
                                class="sales-total-card rounded-lg p-5 text-center min-w-40"
                            >
                                <div
                                    class="text-md font-medium opacity-90 text-white"
                                >
                                    # Unidades
                                </div>
                                <div class="text-4xl font-bold mt-2 text-white">
                                    {{ productCurrent.totalSales }}
                                </div>
                            </div>
                            <div
                                class="sales-total-card rounded-lg p-5 text-center min-w-40"
                            >
                                <div
                                    class="font-medium text-lg opacity-90 text-white"
                                >
                                    Ventas
                                </div>
                                <div class="text-4xl font-bold mt-2 text-white">
                                    {{ productCurrent.totalRevenue | money }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-3 w-full">
                <time-filters v-model="query" label="fecha de desembolso" />
            </div>

            <div class="flex justify-between my-2">
                <div class="flex justify-around">
                    <app-button v-show="query" relief @click="listItems">
                        Consultar 🔎
                    </app-button>
                </div>

                <div class="flex items-end justify-around">
                    <tooltip-button
                        icon="⏬"
                        message="Descargar excel"
                        @click="downloadExcelComponent"
                    />
                    <tooltip-button
                        icon="📕"
                        message="Descargar PDF"
                        @click="downloadPDFComponent"
                    />
                </div>
            </div>
            <div class="w-full mt-3">
                <custom-table
                    :fields="$data.$fields"
                    :items="itemsCurrent"
                    :per-page="itemsCurrent.length"
                    responsive
                    outlined
                    no-border-collapse
                    hover
                    class="w-full"
                >
                    <template #cell(id)="data">
                        <div class="text-center">
                            <a
                                :href="data.item.url"
                                target="_blank"
                                rel="noopener"
                                class="font-semibold text-theme-primary hover:underline"
                            >
                                {{ data.item.id }}
                            </a>
                        </div>
                    </template>

                    <template #cell(barcode)="data">
                        <div class="text-center">
                            <span
                                class="inline-flex items-center gap-2 px-3 py-1 bg-brand-blue bg-opacity-10 rounded text-white font-mono text-sm"
                            >
                                <barcode-icon class="w-4 h-4 text-white" />
                                {{ data.item.barcode || "N/A" }}
                            </span>
                        </div>
                    </template>

                    <template #cell(size)="data">
                        <div class="text-center">
                            <span class="capitalize text-theme-base">
                                {{ data.item.size }}
                            </span>
                        </div>
                    </template>

                    <template #cell(color)="data">
                        <div class="text-center">
                            <span class="capitalize text-theme-base">
                                {{ data.item.color }}
                            </span>
                        </div>
                    </template>

                    <template #cell(priceOffer)="data">
                        <div class="text-center">
                            <span class="font-semibold text-theme-base text-lg">
                                {{ data.item.priceOffer | money }}
                            </span>
                        </div>
                    </template>

                    <template #cell(sales)="data">
                        <div class="text-center">
                            <span
                                class="inline-flex items-center justify-center min-w-badge px-3 py-1 bg-brand-blue text-white rounded font-bold"
                            >
                                {{ data.item.sales }}
                            </span>
                        </div>
                    </template>

                    <template #cell(revenue)="data">
                        <div class="text-center">
                            <span class="font-semibold text-theme-base text-lg">
                                {{ data.item.revenue | money }}
                            </span>
                        </div>
                    </template>
                </custom-table>
            </div>
        </template>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { downloadExcel } from "@/common/lib/excel";
import { generateSalesByProductPDF } from "@/common/lib/pdf";

import BarcodeIcon from "@/components/icons/BarcodeIcon.vue";
import ProductsSearch from "@/components/products/ProductsSearch.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import TimeFilters from "@/components/utils/TimeFilters.vue";
import TooltipButton from "@/components/utils/TooltipButton.vue";

export default {
    name: "ProductsOrder",
    components: {
        TooltipButton,
        BarcodeIcon,
        TimeFilters,
        CustomTable,
        ProductsSearch
    },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    meta: {
        roles: ["administrator"]
    },
    data: () => ({
        query: { agoMonths: 1 },
        $urlFilterSchema: [
            { key: "begin", type: "string" },
            { key: "end", type: "string" },
            { key: "agoDays", type: "number" },
            { key: "agoMonths", type: "number" }
        ],
        itemsCurrent: [],
        idSku: "",
        $fields: [
            { key: "id", label: "ID Ref.", sortable: false },
            { key: "barcode", label: "Código de Barras", sortable: false },
            {
                key: "size",
                label: "Tamaño/Talla/Valor",
                sortable: true
            },
            {
                key: "color",
                label: "Color",
                sortable: false
            },
            {
                key: "priceOffer",
                label: "Precio Actual",
                sortable: true
            },
            {
                key: "sales",
                label: "# Ventas",
                sortable: true
            },
            {
                key: "revenue",
                label: "Ventas",
                sortable: true
            }
        ],

        isLoading: false,
        selectedProduct: { name: "" },
        productCurrent: {
            id: 0,
            name: "",
            sku: "",
            createdAt: "",
            updatedAt: "",
            totalSales: 0,
            totalRevenue: 0
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("order:ListReferences", "/app")))
            return;
        if ("idSku" in this.$route.params) {
            const idSku = this.$route.params.idSku;
            this.idSku = idSku;
            this.listItems();
        }
    },
    methods: {
        ...mapActions("orders", ["listOrdersByProduct"]),
        onProductSelected(product) {
            this.idSku = product.id;
            const url = this.$route.fullPath;
            window.history.pushState(
                { idsku: "references" },
                "Product",
                `${url}/${this.idSku}`
            );
            this.query = { agoMonths: 1 };
            this.listItems();
        },
        async listItems() {
            this.isLoading = true;
            this.$loading.show();
            try {
                const result = await this.listOrdersByProduct({
                    idSku: this.idSku,
                    query: this.query
                });
                this.itemsCurrent = result.references;
                this.productCurrent = {
                    id: result.id,
                    name: result.name,
                    totalSales: result.totalSales,
                    totalRevenue: result.totalRevenue,
                    sku: result.sku,
                    createdAt: result.createdAt,
                    updatedAt: result.updatedAt
                };
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async downloadPDFComponent() {
            this.isGeneratingPDF = true;
            try {
                await generateSalesByProductPDF(
                    this.productCurrent,
                    this.itemsCurrent
                );
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Error al generar PDF",
                    text: error.message || "No se pudo generar el archivo PDF",
                    icon: "error"
                });
            } finally {
                this.isGeneratingPDF = false;
            }
        },
        downloadExcelComponent() {
            const filename = `sales_${this.productCurrent.name.replaceAll(
                " ",
                "_"
            )}
                `
                .substring(0, 31)
                .toLocaleLowerCase();
            downloadExcel({ rows: this.itemsCurrent, filename });
        }
    }
};
</script>

<style scoped>
.product-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    background-color: var(--bg-primary);
    color: var(--color);
    border: 1px solid var(--border-color);
    transition: all 0.2s ease;
}

.product-badge:hover {
    transform: scale(1.05);
    background-color: var(--bg-input);
}

.sales-total-card {
    background: linear-gradient(135deg, var(--color-success) 0%, #02913f 100%);
    box-shadow: 0 4px 15px rgba(2, 145, 63, 0.3);
    transition: all 0.3s ease;
}

.sales-total-card:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(2, 145, 63, 0.4);
}
</style>
